import type {
  CoInsurerFormValue,
  CoInsurersValue,
  CowbellAttachmentValueProps,
  CowbellLayerFormValue,
  PrimaryLayerFormValue,
  PrimaryLayerValueProps,
  TowerFormValue,
  TowerValue,
  TowerValueProps,
  UnderlyingLayerFormValue,
  UnderlyingLayerValueProps,
} from '../types';

export function createEmptyTowerForm(): TowerFormValue {
  return {
    primary: {
      carrier: '',
      policyNumber: '',
      limit: null,
      retention: null,
      premium: null,
      coInsurers: [],
    },
    underlying: [],
    cowbell: { limit: null, premium: null },
  };
}

function readCoInsurers(coInsurers?: CoInsurersValue): CoInsurerFormValue[] {
  return (coInsurers?.values ?? []).map(({ props }) => ({
    carrier: props.carrier.value,
    percentage: props.percentage.value,
  }));
}

export function fromTowerValue(value?: TowerValue | null): TowerFormValue {
  if (!value?.props) {
    return createEmptyTowerForm();
  }

  const { primary, underlying, cowbellAttachment } = value.props;
  const p = primary?.props;
  const primaryForm: PrimaryLayerFormValue = {
    carrier: p?.carrier?.value ?? '',
    policyNumber: p?.policyNumber?.value ?? '',
    limit: p?.limit?.value ?? null,
    retention: p?.retention?.value ?? null,
    premium: p?.premium?.value ?? null,
    coInsurers: readCoInsurers(p?.coInsurers),
    ...(p?.layerPdfDocId ? { layerPdfDocId: p.layerPdfDocId.value } : {}),
  };

  const underlyingForm: UnderlyingLayerFormValue[] = (underlying?.values ?? []).map(
    ({ props: u }) => ({
      carrier: u.carrier?.value ?? '',
      policyNumber: u.policyNumber?.value ?? '',
      limit: u.limit?.value ?? null,
      premium: u.premium?.value ?? null,
      coInsurers: readCoInsurers(u.coInsurers),
      ...(u.layerPdfDocId ? { layerPdfDocId: u.layerPdfDocId.value } : {}),
    })
  );

  const c = cowbellAttachment?.props;
  const cowbellForm: CowbellLayerFormValue = {
    limit: c?.limit?.value ?? null,
    premium: c?.premium?.value ?? null,
    ...(c?.layerPdfDocId ? { layerPdfDocId: c.layerPdfDocId.value } : {}),
  };

  return { primary: primaryForm, underlying: underlyingForm, cowbell: cowbellForm };
}

function writeCoInsurers(coInsurers: CoInsurerFormValue[]): CoInsurersValue | undefined {
  if (coInsurers.length === 0) {
    return undefined;
  }
  return {
    values: coInsurers.map((ci) => ({
      props: {
        carrier: { value: ci.carrier },
        percentage: { value: ci.percentage as number },
      },
    })),
  };
}

function isEntered(value: string | number | null | undefined): value is string | number {
  return value !== null && value !== undefined && value !== '';
}

function hasAnyValue(props: object): boolean {
  return Object.keys(props).length > 0;
}

function buildPrimaryProps(p: PrimaryLayerFormValue): PrimaryLayerValueProps {
  const props: PrimaryLayerValueProps = {};
  if (isEntered(p.carrier)) {
    props.carrier = { value: p.carrier };
  }
  if (isEntered(p.policyNumber)) {
    props.policyNumber = { value: p.policyNumber };
  }
  if (isEntered(p.limit)) {
    props.limit = { value: p.limit };
  }
  if (isEntered(p.retention)) {
    props.retention = { value: p.retention };
  }
  if (isEntered(p.premium)) {
    props.premium = { value: p.premium };
  }
  const coInsurers = writeCoInsurers(p.coInsurers);
  if (coInsurers) {
    props.coInsurers = coInsurers;
  }
  if (p.layerPdfDocId) {
    props.layerPdfDocId = { value: p.layerPdfDocId };
  }
  return props;
}

function buildUnderlyingProps(u: UnderlyingLayerFormValue): UnderlyingLayerValueProps {
  const props: UnderlyingLayerValueProps = {};
  if (isEntered(u.carrier)) {
    props.carrier = { value: u.carrier };
  }
  if (isEntered(u.policyNumber)) {
    props.policyNumber = { value: u.policyNumber };
  }
  if (isEntered(u.limit)) {
    props.limit = { value: u.limit };
  }
  if (isEntered(u.premium)) {
    props.premium = { value: u.premium };
  }
  const coInsurers = writeCoInsurers(u.coInsurers);
  if (coInsurers) {
    props.coInsurers = coInsurers;
  }
  if (u.layerPdfDocId) {
    props.layerPdfDocId = { value: u.layerPdfDocId };
  }
  return props;
}

function buildCowbellProps(cowbell: CowbellLayerFormValue): CowbellAttachmentValueProps {
  const props: CowbellAttachmentValueProps = {};
  if (isEntered(cowbell.limit)) {
    props.limit = { value: cowbell.limit };
  }
  if (isEntered(cowbell.premium)) {
    props.premium = { value: cowbell.premium };
  }
  if (cowbell.layerPdfDocId) {
    props.layerPdfDocId = { value: cowbell.layerPdfDocId };
  }
  return props;
}

export function toTowerValue(form: TowerFormValue): TowerValue {
  const props: TowerValueProps = {};

  const primaryProps = buildPrimaryProps(form.primary);
  if (hasAnyValue(primaryProps)) {
    props.primary = { props: primaryProps };
  }

  const cowbellProps = buildCowbellProps(form.cowbell);
  if (hasAnyValue(cowbellProps)) {
    props.cowbellAttachment = { props: cowbellProps };
  }

  const underlyingValues = form.underlying
    .map(buildUnderlyingProps)
    .filter(hasAnyValue)
    .map((underlyingProps) => ({ props: underlyingProps }));
  if (underlyingValues.length > 0) {
    props.underlying = { values: underlyingValues };
  }

  return { props };
}
